import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPages = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyPage = {
    slug: '',
    title: '',
    meta_description: '',
    meta_keywords: '',
    content: {},
    is_active: true
  };
  
  const [formData, setFormData] = useState(emptyPage);
  const [contentText, setContentText] = useState('');

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pages`);
      setPages(response.data);
    } catch (error) {
      toast.error('Sayfalar yüklenemedi');
    }
    setLoading(false);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData(page);
    setContentText(JSON.stringify(page.content || {}, null, 2));
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingPage(null);
    setFormData(emptyPage);
    setContentText('{}');
    setIsDialogOpen(true);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Bu sayfayı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/pages/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sayfa silindi');
      fetchPages();
    } catch (error) {
      toast.error('Sayfa silinemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    let content = {};
    try {
      content = JSON.parse(contentText);
    } catch (error) {
      toast.error('İçerik JSON formatında olmalı');
      setSaving(false);
      return;
    }

    const data = {
      ...formData,
      content
    };

    try {
      if (editingPage) {
        await axios.put(`${API_URL}/api/pages/${editingPage.slug}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sayfa güncellendi');
      } else {
        await axios.post(`${API_URL}/api/pages`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sayfa oluşturuldu');
      }
      setIsDialogOpen(false);
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-pages">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sayfalar</h1>
          <p className="text-slate-600">Site sayfalarını yönetin ve yeni sayfalar ekleyin.</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="add-page-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Sayfa
        </Button>
      </div>

      <div className="space-y-4">
        {pages.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-600">
              Henüz özel sayfa eklenmemiş. Varsayılan sayfalar otomatik olarak yüklenir.
            </CardContent>
          </Card>
        ) : (
          pages.map((page, index) => (
            <Card key={page.slug} data-testid={`page-item-${index}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <FileText className="w-5 h-5 text-[#002FA7]" />
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{page.title}</h3>
                  <p className="text-sm text-slate-600">/{page.slug}</p>
                </div>
                <div className="flex gap-2">
                  <Link to={`/${page.slug}`} target="_blank">
                    <Button variant="outline" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(page)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(page.slug)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Sayfa Düzenle' : 'Yeni Sayfa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Sayfa Başlığı</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="page-title-input"
                />
              </div>
              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  required
                  disabled={!!editingPage}
                  placeholder="ornek-sayfa"
                  data-testid="page-slug-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Açıklama (SEO)</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={2}
                placeholder="Arama motorlarında görünecek açıklama"
                data-testid="page-meta-desc-input"
              />
            </div>

            <div>
              <Label htmlFor="meta_keywords">Meta Anahtar Kelimeler (SEO)</Label>
              <Input
                id="meta_keywords"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                placeholder="kelime1, kelime2, kelime3"
                data-testid="page-meta-keywords-input"
              />
            </div>

            <div>
              <Label htmlFor="content">Sayfa İçeriği (JSON)</Label>
              <Textarea
                id="content"
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder='{"hero_text": "Başlık", "body": "İçerik..."}'
                data-testid="page-content-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="save-page-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPages;
